import type { UserUuid } from "../types/identity";
interface CacheEntry {
	fileId: string;
	patientId: UserUuid;
	blob: Blob;
	timestamp: number;
	size: number;
}

export class AudioCacheManager {
	private static instance: AudioCacheManager;
	private db: IDBDatabase | null = null;
	private readonly DB_NAME = "AudioCache";
	private readonly STORE_NAME = "audioFiles";
	private readonly MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days

	private constructor() { }

	public static getInstance(): AudioCacheManager {
		if (!AudioCacheManager.instance) {
			AudioCacheManager.instance = new AudioCacheManager();
		}
		return AudioCacheManager.instance;
	}

	public async initialize(): Promise<void> {
		if (this.db) return;

		return new Promise((resolve, reject) => {
			const request = indexedDB.open(this.DB_NAME, 1);

			request.onerror = () => reject(request.error);
			request.onsuccess = () => {
				this.db = request.result;
				resolve();
			};

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;
				if (!db.objectStoreNames.contains(this.STORE_NAME)) {
					const store = db.createObjectStore(this.STORE_NAME, { keyPath: "fileId" });
					store.createIndex("timestamp", "timestamp", { unique: false });
				}
			};
		});
	}

	public async set(entry: CacheEntry): Promise<void> {
		if (!this.db) await this.initialize();

		const transaction = this.db!.transaction([this.STORE_NAME], "readwrite");
		const store = transaction.objectStore(this.STORE_NAME);

		return new Promise((resolve, reject) => {
			const request = store.put(entry);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	public async get(fileId: string): Promise<CacheEntry | null> {
		if (!this.db) await this.initialize();

		const transaction = this.db!.transaction([this.STORE_NAME], "readonly");
		const store = transaction.objectStore(this.STORE_NAME);

		return new Promise((resolve, reject) => {
			const request = store.get(fileId);
			request.onsuccess = () => {
				const entry = request.result as CacheEntry | undefined;
				if (!entry) {
					resolve(null);
					return;
				}
				if (Date.now() - entry.timestamp > this.MAX_AGE) {
					this.delete(fileId);
					resolve(null);
					return;
				}
				resolve(entry);
			};
			request.onerror = () => reject(request.error);
		});
	}

	public async delete(fileId: string): Promise<void> {
		if (!this.db) await this.initialize();

		const transaction = this.db!.transaction([this.STORE_NAME], "readwrite");
		const store = transaction.objectStore(this.STORE_NAME);

		return new Promise((resolve, reject) => {
			const request = store.delete(fileId);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	public async cleanupOld(): Promise<number> {
		if (!this.db) await this.initialize();

		const transaction = this.db!.transaction([this.STORE_NAME], "readwrite");
		const store = transaction.objectStore(this.STORE_NAME);
		const index = store.index("timestamp");

		return new Promise((resolve, reject) => {
			let deletedCount = 0;
			const cutoffTime = Date.now() - this.MAX_AGE;
			const request = index.openCursor();

			request.onsuccess = (event) => {
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					const entry = cursor.value as CacheEntry;
					if (entry.timestamp < cutoffTime) {
						cursor.delete();
						deletedCount++;
					}
					cursor.continue();
				} else {
					resolve(deletedCount);
				}
			};

			request.onerror = () => reject(request.error);
		});
	}

	public async clear(): Promise<void> {
		if (!this.db) await this.initialize();

		const transaction = this.db!.transaction([this.STORE_NAME], "readwrite");
		const store = transaction.objectStore(this.STORE_NAME);

		return new Promise((resolve, reject) => {
			const request = store.clear();
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}
}

export default AudioCacheManager;